import React from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, Cell 
} from 'recharts';
import { Lock, BarChart2, Star } from 'lucide-react';
import { useTranslation } from "../../../contexts/LanguageContext";

const MerchantStatsTab = ({ merchant, isPremiumUser, navigate }) => {
  const { t } = useTranslation();

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const getChartData = () => {
    if (!merchant) return [];
    return merchant.subscriptionPlans.map(plan => ({
      name: plan.name,
      users: plan.userCount,
      percentage: ((plan.userCount / merchant.stats.subscribers) * 100).toFixed(1),
      color: plan.color
    }));
  };

  if (!isPremiumUser) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
          <Lock className="h-6 w-6 text-yellow-600" />
        </div>
        <h3 className="mt-3 text-lg font-medium text-gray-900">
          {t('merchant.statsTab.premiumFeature') || "Premium Feature"}
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          {t('merchant.statsTab.upgradeForStats') || "Detailed statistics are only available to premium users. Upgrade your account to access this feature."}
        </p>
        <div className="mt-6">
          <button
            onClick={() => navigate('/upgrade')}
            className="inline-flex items-center rounded-md border border-transparent bg-yellow-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
          >
            {t('merchant.statsTab.upgradeToPremium') || "Upgrade to Premium"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Plan Distribution Chart - Now on top */}
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-center mb-4">
          <BarChart2 className="h-5 w-5 text-[#253985] mr-2" />
          <h3 className="text-lg font-semibold text-[#253985]">
            {t('merchant.statsTab.planDistribution') || "Plan Distribution"}
          </h3>
        </div>
        
        {merchant.stats.subscribers >= 50 ? (
          <div className="h-96 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={getChartData()}
                layout="vertical"
                margin={{ top: 20, right: 30, left: 40, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={100}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'users') {
                      return [
                        `${formatNumber(value)} ${(t('merchant.statsTab.subscribers') || 'users').toLowerCase()}`,
                        t('merchant.statsTab.subscribers') || 'Subscribers'
                      ];
                    }
                    return [value, name];
                  }}
                  labelFormatter={(label) => `${label} ${t('merchant.common.plan') || 'Plan'}`}
                />
                <Legend />
                <Bar dataKey="users" name={t('merchant.statsTab.subscribers') || "Subscribers"} radius={[0, 4, 4, 0]}>
                  {getChartData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-96 w-full flex items-center justify-center bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <p className="text-gray-500 text-sm">
              {t('merchant.statsTab.noRepresentativeData') || "No representative data"}
            </p>
          </div>
        )}
      </div>

      {/* Key Metrics (without total subscribers) */}
      <h2 className="text-lg font-semibold text-[#253985]">
        {t('merchant.statsTab.keyMetrics') || "Key Metrics"}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Average Rating */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-lg border border-blue-100 shadow-sm">
          <h3 className="text-sm font-medium text-gray-500 mb-1">
            {t('merchant.statsTab.avgRating') || "Average Rating"}
          </h3>
          <div className="flex items-center">
            <div className="text-2xl font-bold text-[#253985] mr-2">
              {merchant.stats.avgRating}
            </div>
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`h-5 w-5 ${i < Math.floor(merchant.stats.avgRating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
                />
              ))}
            </div>
          </div>
        </div>
        
        {/* Satisfaction Rate */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-lg border border-blue-100 shadow-sm">
          <h3 className="text-sm font-medium text-gray-500 mb-1">
            {t('merchant.statsTab.satisfactionRate') || "Satisfaction Rate"}
          </h3>
          <div className="flex items-center">
            <div className="text-2xl font-bold text-[#253985] mr-2">
              {merchant.stats.satisfactionRate}%
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-green-500 h-2.5 rounded-full" 
                style={{ width: `${merchant.stats.satisfactionRate}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Popular Plan (full width) */}
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <h3 className="text-md font-semibold text-[#253985] mb-4">
          {t('merchant.statsTab.popularPlan') || "Popular Plan"}
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xl font-bold text-[#253985]">
              {merchant.subscriptionPlans[0]?.name || t('merchant.statsTab.standard') || "Standard"}
            </p>
            <p className="text-sm text-gray-500">
              {t('merchant.statsTab.mostSubscribed') || "Most subscribed"}
            </p>
          </div>
          <div className="bg-blue-100 p-3 rounded-full">
            <Star className="h-6 w-6 text-[#253985]" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MerchantStatsTab;